import React, { useState } from 'react';
import { FaQuestionCircle, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import Layout from '../components/Layout';
import SEO from '../components/SEO';

const FAQ: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const faqs = [
    {
      question: 'Comment choisir le meilleur bookmaker ?',
      answer: 'Nous recommandons de comparer les cotes, les bonus, les méthodes de paiement et les avis des utilisateurs. Notre guide vous aide à faire le meilleur choix selon vos besoins. Chaque bookmaker a ses propres avantages : certains offrent de meilleures cotes, d\'autres des bonus plus généreux, et certains se distinguent par leur interface utilisateur.',
    },
    {
      question: 'Les bookmakers sont-ils sûrs et légaux ?',
      answer: 'Oui, tous les bookmakers que nous recommandons sont licenciés par l\'Autorité Nationale des Jeux (ANJ) et respectent les réglementations françaises en matière de paris sportifs. Ils utilisent des systèmes de cryptage avancés pour protéger vos données personnelles et vos transactions financières.',
    },
    {
      question: 'Combien de temps pour recevoir mes gains ?',
      answer: 'Les délais varient selon le bookmaker et la méthode de retrait. En général, les retraits sont traités entre 12h et 48h. Les cartes bancaires sont généralement plus rapides que les virements. Winamax propose des retraits en 12h, tandis que la plupart des autres bookmakers traitent les retraits en 24h.',
    },
    {
      question: 'Puis-je annuler un pari ?',
      answer: 'Cela dépend du bookmaker et du type de pari. Certains offrent la fonctionnalité "Cash-out" qui permet de clôturer votre pari avant la fin de l\'événement. Cette option vous permet de sécuriser vos gains ou de limiter vos pertes en fonction de l\'évolution du match.',
    },
    {
      question: 'Y a-t-il des frais sur les retraits ?',
      answer: 'La plupart des bookmakers français n\'appliquent pas de frais sur les retraits, mais cela peut varier selon la méthode choisie. Les cartes bancaires et PayPal sont généralement gratuits, tandis que certains virements bancaires peuvent avoir des frais. Vérifiez toujours les conditions avant de retirer.',
    },
    {
      question: 'Comment fonctionnent les bonus de bienvenue ?',
      answer: 'Les bonus de bienvenue sont des offres promotionnelles offertes aux nouveaux clients. Généralement, le bookmaker double votre premier dépôt jusqu\'à un certain montant. Par exemple, si vous déposez 50€, vous recevez 50€ supplémentaires en bonus. Vous devez ensuite miser ce bonus un certain nombre de fois avant de pouvoir le retirer.',
    },
    {
      question: 'Puis-je avoir plusieurs comptes chez différents bookmakers ?',
      answer: 'Oui, vous pouvez avoir des comptes chez plusieurs bookmakers. Cela vous permet de comparer les cotes et de choisir les meilleures offres pour chaque pari. C\'est une stratégie recommandée pour maximiser vos gains potentiels.',
    },
    {
      question: 'Quels sont les sports les plus populaires pour les paris ?',
      answer: 'En France, le football est de loin le sport le plus populaire pour les paris, suivi par le tennis, le basketball et le rugby. Les bookmakers offrent généralement les meilleures cotes et le plus grand nombre de marchés pour ces sports.',
    },
    {
      question: 'Comment puis-je contacter le support client ?',
      answer: 'Tous les bookmakers recommandés offrent plusieurs moyens de contact : chat en direct, email et téléphone. Les temps de réponse varient, mais la plupart répondent dans les 24 heures. Le chat en direct est généralement le moyen le plus rapide pour obtenir une réponse immédiate.',
    },
    {
      question: 'Que faire en cas de problème avec un bookmaker ?',
      answer: 'Si vous rencontrez un problème avec un bookmaker, contactez d\'abord leur service client. Si le problème persiste, vous pouvez contacter l\'ANJ (Autorité Nationale des Jeux) qui régule les paris en ligne en France. Ils peuvent vous aider à résoudre les litiges avec les opérateurs agréés.',
    },
  ];

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <Layout>
      <SEO
        title="Questions Fréquentes - Guide des Paris Sportifs"
        description="Trouvez les réponses à toutes vos questions sur les paris sportifs en France. FAQ complète sur les bookmakers, bonus, retraits et plus encore."
        url="/faq"
        jsonLd={jsonLd}
      />

      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 via-purple-600/10 to-pink-600/10"></div>
        <div className="relative z-10 container mx-auto px-4 text-center">
          <div className="inline-block mb-6 px-4 py-2 glass-card rounded-full text-sm font-semibold border border-yellow-500/30">
            <FaQuestionCircle className="inline mr-2 text-yellow-400" />
            Questions & Réponses
          </div>
          <h1 className="text-5xl md:text-6xl font-black mb-6 heading-font">
            <span className="text-gradient">Questions Fréquentes</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Trouvez rapidement les réponses à toutes vos questions sur les paris sportifs en France
          </p>
        </div>
      </section>

      {/* FAQ List */}
      <section className="py-12 pb-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="glass-card rounded-2xl overflow-hidden transition-all duration-300"
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full flex items-center justify-between p-6 md:p-8 text-left hover:bg-white/5 transition-colors"
                >
                  <div className="flex items-start space-x-4 flex-1">
                    <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-purple-500 rounded-xl flex items-center justify-center flex-shrink-0">
                      <FaQuestionCircle className="text-white" />
                    </div>
                    <h3 className="text-lg md:text-xl font-bold heading-font flex-1">{faq.question}</h3>
                  </div>
                  <div className="ml-4 flex-shrink-0">
                    {openIndex === index ? (
                      <FaChevronUp className="text-cyan-400" />
                    ) : (
                      <FaChevronDown className="text-gray-400" />
                    )}
                  </div>
                </button>
                {openIndex === index && (
                  <div className="px-6 md:px-8 pb-6 md:pb-8">
                    <div className="pl-14">
                      <p className="text-gray-300 leading-relaxed">{faq.answer}</p>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default FAQ;

